import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Rating,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  pathwayItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

// ローカルデータをロード（初回のみ）
let pharmacologyDataCache = null;
const loadPharmacologyData = async () => {
  if (pharmacologyDataCache) {
    return pharmacologyDataCache;
  }
  
  try {
    const response = await fetch('/pharmacology-data.json');
    if (!response.ok) {
      console.error('Failed to load pharmacology data');
      return null;
    }
    pharmacologyDataCache = await response.json();
    return pharmacologyDataCache;
  } catch (error) {
    console.error('Error loading pharmacology data:', error);
    return null;
  }
};

// KampoDBデータを取得（ローカルデータから）
const fetchKampoDBData = async (formulaId, dataType) => {
  try {
    const allData = await loadPharmacologyData();
    if (!allData || !allData[formulaId]) {
      console.warn(`No data found for formula: ${formulaId}`);
      return null;
    }
    
    const formulaData = allData[formulaId];
    return formulaData[dataType] || null;
  } catch (error) {
    console.error(`Error fetching ${dataType} for ${formulaId}:`, error);
    return null;
  }
};

// 薬理作用スコアを計算（改善版）
const calculatePharmacologyScore = (pathways) => {
  if (!pathways || pathways.length === 0) return {};
  
  const scores = {
    antiInflammatory: 0,
    analgesic: 0,
    immunomodulatory: 0,
    antioxidant: 0,
  };
  
  let matchCount = {
    antiInflammatory: 0,
    analgesic: 0,
    immunomodulatory: 0,
    antioxidant: 0,
  };
  
  pathways.forEach(pathway => {
    const name = pathway.name.toLowerCase();
    const fdr = parseFloat(pathway.fdr) || 1;
    const count = parseInt(pathway.count) || 0;
    // FDR値が小さいほど有意性が高い（スコアが高い）
    const significance = Math.max(0, Math.min(10, -Math.log10(fdr)));
    
    // 抗炎症作用
    if (name.includes('inflammation') || name.includes('inflammatory') ||
        name.includes('tnf') || name.includes('nf-kappa') || 
        name.includes('il-17') || name.includes('rheumatoid')) {
      scores.antiInflammatory += significance * (count / 100);
      matchCount.antiInflammatory++;
    }
    
    // 鎮痛作用（痛み関連パスウェイは少ないため、神経系も含む）
    if (name.includes('pain') || name.includes('nociceptor') || 
        name.includes('opioid') || name.includes('neurotrophin') ||
        name.includes('serotonergic')) {
      scores.analgesic += significance * (count / 100);
      matchCount.analgesic++;
    }
    
    // 免疫調整作用
    if (name.includes('immune') || name.includes('t cell') || 
        name.includes('b cell') || name.includes('cytokine') ||
        name.includes('leukocyte') || name.includes('lymphocyte')) {
      scores.immunomodulatory += significance * (count / 100);
      matchCount.immunomodulatory++;
    }
    
    // 抗酸化作用
    if (name.includes('oxidative') || name.includes('antioxidant') || 
        name.includes('reactive oxygen') || name.includes('peroxid')) {
      scores.antioxidant += significance * (count / 100);
      matchCount.antioxidant++;
    }
  });
  
  // スコアを0-5のスケールに正規化（マッチ数で平均化）
  Object.keys(scores).forEach(key => {
    if (matchCount[key] > 0) {
      // マッチしたパスウェイの平均スコアを計算
      const avgScore = scores[key] / matchCount[key];
      // 0-5のスケールに収める
      scores[key] = Math.min(5, Math.max(0, Math.round(avgScore)));
    } else {
      scores[key] = 0;
    }
  });
  
  return scores;
};

// ターゲットタンパク質を抽出（改善版）
const extractTargetProteins = (proteins) => {
  if (!proteins || proteins.length === 0) return [];
  
  // 重要な炎症・免疫関連タンパク質
  const importantProteinPatterns = [
    // COX-2 (シクロオキシゲナーゼ2)
    { pattern: /^PTGS2$/i, display: 'COX-2 (PTGS2)' },
    // TNF-α
    { pattern: /^TNF$/i, display: 'TNF-α' },
    // インターロイキン
    { pattern: /^IL(1B|6|10|17|18)$/i, display: null },
    // NF-κB関連
    { pattern: /^NFKB/i, display: null },
    { pattern: /^RELA$/i, display: 'NF-κB (RELA)' },
    // iNOS
    { pattern: /^NOS2$/i, display: 'iNOS (NOS2)' },
    // その他の重要タンパク
    { pattern: /^(MAPK14|MAPK1|MAPK3|MAPK8)$/i, display: null }, // MAPK
    { pattern: /^(AKT1|PIK3)/, display: null }, // PI3K-AKT
    { pattern: /^(CASP3|CASP8|CASP9)$/i, display: null }, // カスパーゼ
    { pattern: /^(BCL2|BAX)$/i, display: null }, // アポトーシス
    { pattern: /^(STAT3|JAK2)$/i, display: null }, // JAK-STAT
    { pattern: /^(VEGFA|HIF1A)$/i, display: null }, // 血管新生
    { pattern: /^(TP53|CDKN1A)$/i, display: null }, // 細胞周期
  ];
  
  const matched = [];
  
  proteins.forEach(protein => {
    for (const { pattern, display } of importantProteinPatterns) {
      if (pattern.test(protein.name)) {
        matched.push({
          ...protein,
          displayName: display || protein.name
        });
        break;
      }
    }
  });
  
  return matched.slice(0, 15);
};

function PharmacologyPanel({ tsumuraKampoIds }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pharmacologyData, setPharmacologyData] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!tsumuraKampoIds || tsumuraKampoIds.length === 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // 各ツムラ漢方のデータを取得
        const allPathways = [];
        const allProteins = [];
        
        for (const id of tsumuraKampoIds) {
          const [pathways, proteins] = await Promise.all([
            fetchKampoDBData(id, 'pathway'),
            fetchKampoDBData(id, 'protein'),
          ]);
          
          if (pathways) allPathways.push(...pathways);
          if (proteins) allProteins.push(...proteins);
        }
        
        // 重複を除去し、カウントでソート
        const uniquePathways = Array.from(
          allPathways.reduce((map, p) => {
            if (!map.has(p.id)) {
              map.set(p.id, p);
            }
            return map;
          }, new Map()).values()
        ).sort((a, b) => parseInt(b.count) - parseInt(a.count));
        
        const uniqueProteins = Array.from(
          allProteins.reduce((map, p) => {
            if (!map.has(p.id)) {
              map.set(p.id, p);
            }
            return map;
          }, new Map()).values()
        );
        
        // スコアを計算
        const scores = calculatePharmacologyScore(uniquePathways);
        const targetProteins = extractTargetProteins(uniqueProteins);
        
        // データが少なくても表示する
        if (uniquePathways.length === 0 && uniqueProteins.length === 0) {
          setError('この組み合わせのKampoDBデータが見つかりませんでした');
        } else {
          setPharmacologyData({
            scores,
            topPathways: uniquePathways.slice(0, 10),
            targetProteins,
          });
        }
        
      } catch (err) {
        console.error('Error fetching pharmacology data:', err);
        setError('データの取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [tsumuraKampoIds]);
  
  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }
  
  if (!pharmacologyData) {
    return null;
  }
  
  const { scores, topPathways, targetProteins } = pharmacologyData;
  
  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          この組み合わせの薬理作用（推定）
        </Typography>
        
        {/* 薬理作用スコア */}
        <Box mt={2}>
          <Typography className={classes.sectionTitle}>
            主な薬理作用
          </Typography>
          
          <Box mt={1}>
            <Typography variant="body2">抗炎症作用</Typography>
            <Rating value={scores.antiInflammatory} max={5} readOnly />
          </Box>
          
          <Box mt={1}>
            <Typography variant="body2">鎮痛作用</Typography>
            <Rating value={scores.analgesic} max={5} readOnly />
          </Box>
          
          <Box mt={1}>
            <Typography variant="body2">免疫調整作用</Typography>
            <Rating value={scores.immunomodulatory} max={5} readOnly />
          </Box>
          
          <Box mt={1}>
            <Typography variant="body2">抗酸化作用</Typography>
            <Rating value={scores.antioxidant} max={5} readOnly />
          </Box>
        </Box>
        
        <Divider style={{ margin: '16px 0' }} />
        
        {/* ターゲットタンパク質 */}
        {targetProteins.length > 0 && (
          <Box mt={2}>
            <Typography className={classes.sectionTitle}>
              主要ターゲットタンパク質
            </Typography>
            <Box>
              {targetProteins.map((protein) => (
                <Chip
                  key={protein.id}
                  label={protein.displayName || protein.name}
                  size="small"
                  className={classes.chip}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        
        <Divider style={{ margin: '16px 0' }} />
        
        {/* 関連パスウェイ */}
        {topPathways.length > 0 && (
          <Box mt={2}>
            <Typography className={classes.sectionTitle}>
              関連する生物学的パスウェイ（上位10件）
            </Typography>
            <List dense>
              {topPathways.map((pathway) => (
                <ListItem key={pathway.id} className={classes.pathwayItem}>
                  <ListItemText
                    primary={pathway.name}
                    secondary={`関連タンパク質: ${pathway.count}件`}
                  />
                  <Chip
                    label={`FDR: ${parseFloat(pathway.fdr).toExponential(1)}`}
                    size="small"
                    color={parseFloat(pathway.fdr) < 0.001 ? 'success' : 'default'}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            ※ この情報は、KampoDB（富山大学和漢医薬学総合研究所）のデータに基づいた推定値です。
            実際の臨床効果を保証するものではありません。
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default PharmacologyPanel;
